'use client';

import React from 'react';
import { cn } from '@/lib/cn';
import {
  SectionHeader,
  RatingCell,
  Divider,
  SectionContainer,
  SectionContent,
} from '@/components/ui';
import {
  IconBroom,
  IconTrash,
  IconTemperature,
  IconGroup,
  IconVolumeUp,
  IconConversation,
  IconBug,
  IconWaterTap,
} from '@repo/svgs';
import { reviewInfo, type ReviewIconName } from '@repo/utils';

// Icon mapping for web
const iconMap: Record<ReviewIconName, React.ComponentType<{ className?: string }>> = {
  IconBroom,
  IconTrash,
  IconTemperature,
  IconGroup,
  IconVolumeUp,
  IconConversation,
  IconBug,
  IconWaterTap,
};

interface RatingsProps {
  ratings: Record<string, number> | null;
  isMasked: boolean;
  title?: string;
}

export function Ratings({ ratings, isMasked, title = 'Building ratings' }: RatingsProps) {
  // Always show the section - if masked, show masked state; if not masked and no ratings, show empty state

  return (
    <SectionContainer>
      {/* Section Header */}
      <SectionHeader title={title} />

      {/* Content Container */}

      {/* Mobile: Single column layout */}
      <div className="flex flex-col gap-1 md:hidden">
        {reviewInfo.map((item, index) => {
          const value = ratings?.[item.aggKey] ?? null;
          const isLast = index === reviewInfo.length - 1;

          return (
            <React.Fragment key={`mobile-${item.title}`}>
              <RatingCell
                title={item.title}
                rating={value || 0}
                icon={iconMap[item.iconName]}
                isMasked={isMasked}
              />
              {!isLast && (
                <div className="px-2 py-0.5">
                  <Divider />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Desktop: 2-column grid layout */}
      <div className="hidden md:flex md:flex-col md:gap-1 pl-2">
        {Array.from({ length: Math.ceil(reviewInfo.length / 2) }, (_, rowIndex) => {
          const leftIndex = rowIndex * 2;
          const rightIndex = leftIndex + 1;
          const leftItem = reviewInfo[leftIndex];
          const rightItem = reviewInfo[rightIndex];
          const isLastRow = rowIndex === Math.ceil(reviewInfo.length / 2) - 1;

          // Skip if leftItem doesn't exist (shouldn't happen but for type safety)
          if (!leftItem) return null;

          return (
            <React.Fragment key={`desktop-row-${rowIndex}`}>
              <div className="flex gap-4">
                {/* Left item */}
                <RatingCell
                  className="flex-1"
                  title={leftItem.title}
                  rating={ratings?.[leftItem.aggKey] || 0}
                  icon={iconMap[leftItem.iconName]}
                  isMasked={isMasked}
                />

                {/* Right item */}
                {rightItem && (
                  <RatingCell
                    className="flex-1"
                    title={rightItem.title}
                    rating={ratings?.[rightItem.aggKey] || 0}
                    icon={iconMap[rightItem.iconName]}
                    isMasked={isMasked}
                  />
                )}
              </div>

              {/* Divider between rows */}
              {!isLastRow && (
                <div className="px-2 py-0.5">
                  <Divider />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </SectionContainer>
  );
}
